import { formatCurrency } from '../../utils/currency';

interface FeeAccuracyProps {
  totalEstimated: number;
  totalActual: number;
  delta: number;
  deltaPct: number | null;
  count: number;
  overCount: number;
  underCount: number;
  matchCount: number;
  loading?: boolean;
  error?: boolean;
  forbidden?: boolean;
  periodLabel?: string;
  onRetry?: () => void;
}

export function FeeAccuracy({
  totalEstimated,
  totalActual,
  delta,
  deltaPct,
  count,
  overCount,
  underCount,
  matchCount,
  loading = false,
  error = false,
  forbidden = false,
  periodLabel,
  onRetry,
}: FeeAccuracyProps) {

  // Forbidden state
  if (forbidden) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Fee accuracy</h3>
        </div>
        <div className="px-4 pt-4 pb-3 flex items-center justify-center min-h-[120px]">
          <p className="text-ink-4 text-[13px]">You don't have access to this.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Fee accuracy</h3>
        </div>
        <div className="px-4 pt-4 pb-3">
          <p className="text-[13px] text-ink-4 mb-3">Couldn't load fee data.</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-[11.5px] font-medium text-tide hover:text-tide/80"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // Loading skeleton
  if (loading && count === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <div className="h-4 w-24 bg-canvas animate-pulse rounded" />
        </div>
        <div className="px-4 pt-4 pb-3 space-y-3">
          <div className="h-8 w-20 bg-canvas animate-pulse rounded" />
          <div className="h-3 w-32 bg-canvas animate-pulse rounded" />
          <div className="flex gap-4 mt-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-3 w-16 bg-canvas animate-pulse rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (count === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <div className="flex items-center gap-2">
            <h3 className="text-[14px] font-semibold">Fee accuracy</h3>
            {periodLabel && (
              <span className="text-[11.5px] text-ink-4">&middot; {periodLabel.toLowerCase()}</span>
            )}
          </div>
        </div>
        <div className="px-4 pt-4 pb-3 flex flex-col items-center justify-center min-h-[120px] gap-2">
          <p className="text-[11.5px] text-ink-4">
            No closed sessions with pricing data in this period.
          </p>
        </div>
      </div>
    );
  }

  // Delta direction: positive means actual > estimated (charged more than quoted)
  const deltaColor = delta > 0 ? 'text-sage' : delta < 0 ? 'text-rust' : 'text-ink-4';
  const deltaSign = delta > 0 ? '+' : '';

  return (
    <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 hair-b">
        <div className="flex items-center gap-2">
          <h3 className="text-[14px] font-semibold">Fee accuracy</h3>
          {periodLabel && (
            <span className="text-[11.5px] text-ink-4">&middot; {periodLabel.toLowerCase()}</span>
          )}
        </div>
      </div>

      <div className="px-4 pt-4 pb-3">
        {/* Main delta */}
        <div className="flex items-baseline gap-2 mb-1">
          <span className={`text-[28px] font-semibold leading-none num ${deltaColor}`}>
            {deltaSign}${formatCurrency(Math.abs(delta))}
          </span>
          {deltaPct !== null && (
            <span className={`text-[11.5px] font-medium ${deltaColor}`}>
              {deltaSign}{deltaPct.toFixed(1)}%
            </span>
          )}
        </div>
        <p className="text-[11.5px] text-ink-4 mb-4">
          actual vs estimated across {count} closed session{count !== 1 ? 's' : ''}
        </p>

        {/* Estimated vs Actual */}
        <div className="flex items-center justify-between mb-3 text-[12px]">
          <div>
            <span className="text-ink-4">Estimated</span>
            <span className="ml-2 font-medium num">${formatCurrency(totalEstimated)}</span>
          </div>
          <div>
            <span className="text-ink-4">Actual</span>
            <span className="ml-2 font-medium num">${formatCurrency(totalActual)}</span>
          </div>
        </div>

        {/* Distribution */}
        <div className="flex items-center gap-4 text-[11.5px]">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-sage shrink-0" />
            <span className="text-ink-3">Over</span>
            <span className="num font-medium">{overCount}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-ink-4 shrink-0" />
            <span className="text-ink-3">Match</span>
            <span className="num font-medium">{matchCount}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-rust shrink-0" />
            <span className="text-ink-3">Under</span>
            <span className="num font-medium">{underCount}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
